import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { Share2, UserPlus, AlarmClock, CalendarClock, ArrowRight, Check, Loader2, ListChecks, RefreshCw } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { stripHtml } from '../../lib/text';
import { useAuth } from '../../contexts/AuthContext';
import { canViewAllLeads, canViewAllProperties } from '../../utils/permissions';
import { shareRenewalCutoffISO, SHARE_RENEWAL_WINDOW_DAYS } from '../../lib/share-renewal';
import { completeFollowUp, rescheduleFollowUp, morningIn } from '../../utils/followups';
import { CollapsibleSection } from './CollapsibleSection';
import { HoverTip } from '../ui/HoverTip';

interface TodoRequirement { id: string; name: string; }
interface TodoAppointment { id: string; title: string; date: string; time: string; type: string; contact_name: string | null; }
interface AgentTodos {
  has_agency: boolean;
  can_share?: boolean;
  scope?: string;
  unshared_requirements?: { count: number; items: TodoRequirement[] };
  new_leads?: number;
  pending_followups?: number;
  upcoming_appointments?: { count: number; items: TodoAppointment[] };
  properties_with_matches?: number;
  requirements_with_matches?: number;
}

interface OverdueContact { id: string; name: string; next_follow_up_at: string; next_follow_up_note: string | null; }

const APPT_LABEL: Record<string, string> = {
  visita: 'Visita', llamada: 'Llamada', reunion: 'Reunión', seguimiento: 'Seguimiento', cierre: 'Cierre',
};

function overdueSince(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return 'hoy';
  return days === 1 ? 'hace 1 día' : `hace ${days} días`;
}

function apptWhen(date: string, time: string): string {
  const d = new Date(`${date}T${(time || '00:00:00').slice(0, 8)}`);
  const day = d.toLocaleDateString('es-GT', { weekday: 'short', day: '2-digit', month: 'short' });
  const hm = (time || '').slice(0, 5);
  return `${day} · ${hm}`;
}

export const PendientesSection: React.FC<{ theme?: 'light' | 'dark'; staggerIndex?: number }> = ({ theme = 'dark', staggerIndex = 0 }) => {
  const isLight = theme !== 'dark';
  const navigate = useNavigate();
  const { profile, realProfile } = useAuth();
  // Days the snooze button adds — per-user pref (Ajustes de usuario), default 7.
  const snoozeDays = Number(realProfile?.ui_preferences?.followup_snooze_days) || 7;
  const [todos, setTodos] = useState<AgentTodos | null>(null);
  const [sharingId, setSharingId] = useState<string | null>(null);
  const [sharedIds, setSharedIds] = useState<Set<string>>(new Set());
  const [overdueList, setOverdueList] = useState<OverdueContact[] | null>(null);
  const [shareRenewals, setShareRenewals] = useState(0);
  const [actingId, setActingId] = useState<string | null>(null);

  const fetchTodos = useCallback(async () => {
    if (!supabase) return;
    const { data, error } = await supabase.rpc('get_agent_todos');
    if (!error && data) setTodos(data as AgentTodos);
  }, []);

  // The RPC only returns a COUNT of overdue follow-ups; users complained they
  // could never see WHICH ones. Fetch the actual contacts and drive the
  // visible number from this list so count and list can never disagree.
  // Explicit own-scope filter: RLS alone is broader than "my follow-ups"
  // (e.g. it lets an agent read owners of their properties), so mirror the
  // RPC's agent_id/created_by scoping for agents without can_view_all_leads.
  const fetchOverdue = useCallback(async () => {
    if (!supabase || !profile) return;
    let query = supabase
      .from('contacts')
      .select('id, name, next_follow_up_at, next_follow_up_note')
      .eq('is_deleted', false)
      .not('next_follow_up_at', 'is', null)
      .lte('next_follow_up_at', new Date().toISOString())
      .order('next_follow_up_at', { ascending: true })
      .limit(50);
    if (profile.role === 'agent' && !canViewAllLeads(profile)) {
      query = query.or(`agent_id.eq.${profile.id},created_by.eq.${profile.id}`);
    }
    const { data, error } = await query;
    if (!error && data) setOverdueList(data as OverdueContact[]);
  }, [profile]);

  // Shares lapsing (or already lapsed) in the network. Not part of
  // get_agent_todos: a count-only query is cheaper than widening that RPC, and
  // it uses the same cutoff as the Propiedades chip so both agree.
  const fetchShareRenewals = useCallback(async () => {
    if (!supabase || !profile) return;
    let query = supabase
      .from('properties')
      .select('id', { count: 'exact', head: true })
      .eq('is_deleted', false)
      .in('share_status', ['shared', 'expired'])
      .lte('share_expires_at', shareRenewalCutoffISO());
    // Mirror the Propiedades list scope: own-only for restricted agents.
    if (profile.role === 'agent' && !canViewAllProperties(profile)) {
      query = query.eq('agent_id', profile.id);
    }
    const { count, error } = await query;
    if (!error) setShareRenewals(count ?? 0);
  }, [profile]);

  useEffect(() => { fetchTodos(); fetchOverdue(); fetchShareRenewals(); }, [fetchTodos, fetchOverdue, fetchShareRenewals]);

  const handleComplete = async (c: OverdueContact) => {
    if (!profile?.agency_id) return;
    setActingId(c.id);
    const err = await completeFollowUp({
      contactId: c.id, agencyId: profile.agency_id, profileId: profile.id, note: c.next_follow_up_note,
    });
    setActingId(null);
    if (err) { alert(`Error al completar: ${err}`); return; }
    setOverdueList(prev => prev ? prev.filter(x => x.id !== c.id) : prev);
  };

  const handleReschedule = async (c: OverdueContact, days: number) => {
    setActingId(c.id);
    const err = await rescheduleFollowUp(c.id, morningIn(days));
    setActingId(null);
    if (err) { alert(`Error al reprogramar: ${err}`); return; }
    setOverdueList(prev => prev ? prev.filter(x => x.id !== c.id) : prev);
  };

  const shareRequirement = async (id: string) => {
    if (!supabase) return;
    setSharingId(id);
    const { data, error } = await supabase.rpc('renew_requirement_sharing', { p_requirement_id: id });
    setSharingId(null);
    if (error) { alert(`Error: ${error.message}`); return; }
    if (data && data.success === false) { alert(data.reason || 'No se pudo compartir'); return; }
    setSharedIds(prev => new Set(prev).add(id));
  };

  if (!todos || !todos.has_agency) return null;

  const unshared = todos.unshared_requirements ?? { count: 0, items: [] };
  const pendingShareItems = unshared.items.filter(i => !sharedIds.has(i.id));
  const remainingUnshared = Math.max(0, unshared.count - sharedIds.size);
  const appts = todos.upcoming_appointments ?? { count: 0, items: [] };
  // Prefer the fetched list length over the RPC count: the number users see
  // must match the rows they can act on below.
  const overdue = overdueList !== null ? overdueList.length : (todos.pending_followups ?? 0);

  const reqMatches = todos.requirements_with_matches ?? 0;
  const propMatches = todos.properties_with_matches ?? 0;
  const totalMatches = reqMatches + propMatches;

  // Zero-value cards are noise, not to-dos — only actionable items render.
  // Matches get the hero banner below instead of a grid card.
  const cards = [
    {
      key: 'reqs', label: 'Requerimientos sin compartir', value: remainingUnshared,
      icon: Share2, color: 'amber' as const, onClick: () => navigate('/requerimientos'),
      hint: 'Compártelos para activar coincidencias',
      tip: 'Compartir tus requerimientos dispara las probabilidades de cierre: la colaboración entre agencias es el corazón del negocio inmobiliario, y en la red tu cliente encuentra la propiedad ideal mucho más rápido.',
    },
    {
      key: 'leads', label: 'Prospectos nuevos', value: todos.new_leads ?? 0,
      icon: UserPlus, color: 'indigo' as const, onClick: () => navigate('/prospectos'),
      hint: 'Aún sin atender',
    },
    {
      key: 'follow', label: 'Seguimientos vencidos', value: overdue,
      icon: AlarmClock, color: 'rose' as const, onClick: () => navigate('/prospectos'),
      hint: 'Con recordatorio pendiente',
      tip: 'La mayoría de las ventas se cierran tras varios seguimientos. Retomar el contacto a tiempo mantiene viva la relación y evita que el prospecto se enfríe.',
    },
    {
      key: 'appts', label: 'Próximas citas (7 días)', value: appts.count,
      icon: CalendarClock, color: 'emerald' as const, onClick: () => navigate('/calendario'),
      hint: 'En tu agenda',
    },
    {
      key: 'shares', label: 'Compartidas por vencer', value: shareRenewals,
      icon: RefreshCw, color: 'amber' as const,
      onClick: () => navigate('/propiedades?filtro=compartir-vence'),
      hint: 'Revisa cada una antes de renovar',
      tip: `Compartir en la red vence a los 180 días (venta) o 90 (alquiler). Estas ya vencieron o vencen en menos de ${SHARE_RENEWAL_WINDOW_DAYS} días: al vencer salen de la red y dejan de generar coincidencias. Revisa precio, disponibilidad y el visto bueno del propietario antes de volver a compartir.`,
    },
  ].filter(c => c.value > 0);

  const allClear = cards.length === 0 && totalMatches === 0;

  const showShareList = remainingUnshared > 0 && !!todos.can_share && pendingShareItems.length > 0;
  const showOverdueList = !!overdueList && overdueList.length > 0;
  const showAgenda = appts.items.length > 0;
  // Two-column layout only when there are detail lists to fill the right side;
  // otherwise the stat cards keep the full width.
  const hasLists = showShareList || showOverdueList || showAgenda;

  const colorMap = {
    amber: { icon: 'bg-amber-500/10 text-amber-500', num: isLight ? 'text-amber-600' : 'text-amber-400', border: isLight ? 'border-amber-200' : 'border-amber-500/20' },
    indigo: { icon: 'bg-indigo-500/10 text-indigo-500', num: isLight ? 'text-indigo-600' : 'text-indigo-400', border: isLight ? 'border-slate-100' : 'border-white/5' },
    rose: { icon: 'bg-rose-500/10 text-rose-500', num: isLight ? 'text-rose-600' : 'text-rose-400', border: isLight ? 'border-rose-200' : 'border-rose-500/20' },
    emerald: { icon: 'bg-emerald-500/10 text-emerald-500', num: isLight ? 'text-emerald-600' : 'text-emerald-400', border: isLight ? 'border-slate-100' : 'border-white/5' },
  };

  return (
    <CollapsibleSection
      title="Pendientes de hoy"
      icon={ListChecks}
      storageKey="dash_pendientes"
      theme={theme}
      staggerIndex={staggerIndex}
      badge={overdue > 0 ? { text: `${overdue} vencido${overdue === 1 ? '' : 's'}`, tone: 'red' } : undefined}
    >
      {/* Two-column layout on lg when detail lists exist: narrow rail with the
          match banner + stat cards, wider column with the actionable lists.
          Without lists everything keeps the full width and stacks as before. */}
      <div className={clsx(hasLists && 'lg:grid lg:grid-cols-5 lg:gap-4 lg:items-start')}>
      <div className={clsx('space-y-4', hasLists && 'lg:col-span-2')}>
      {/* Celebration hero — cross-agency matches are the payoff of the whole
          Intercambio loop, so they outrank every other pendiente. */}
      {totalMatches > 0 && (
        <button
          onClick={() => navigate('/coincidencias')}
          className={clsx(
            'w-full flex flex-wrap items-center gap-3 px-4 py-4 rounded-2xl text-left transition-all hover:shadow-lg',
            'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md shadow-indigo-600/20'
          )}
        >
          <span className="text-2xl leading-none">🎉</span>
          <span className="flex-1 min-w-[200px]">
            <span className="block font-bold text-sm">
              {totalMatches === 1 ? 'Tienes 1 coincidencia de la red esperándote' : `Tienes ${totalMatches} coincidencias de la red esperándote`}
            </span>
            <span className="block text-xs text-white/85 mt-0.5">
              {reqMatches > 0 && propMatches > 0
                ? 'Propiedades para tus clientes y clientes para tus propiedades.'
                : reqMatches > 0 ? 'Otras agencias tienen propiedades que calzan con tus requerimientos.' : 'Requerimientos de otras agencias calzan con tus propiedades.'}
            </span>
          </span>
          <span className="flex items-center gap-1 shrink-0 bg-white/15 px-3 py-1.5 rounded-lg text-xs font-bold">
            Ver coincidencias <ArrowRight size={13} />
          </span>
        </button>
      )}

      {allClear && (
        <div className={clsx(
          'flex items-center gap-2 px-5 py-4 rounded-2xl text-sm font-bold',
          isLight ? 'bg-emerald-50 text-emerald-700' : 'bg-emerald-500/10 text-emerald-300'
        )}>
          <Check size={16} /> Todo al día — sin pendientes por ahora.
        </div>
      )}

      {cards.length > 0 && (
      <div className={clsx('grid grid-cols-2 gap-3', hasLists ? '' : 'lg:grid-cols-3 sm:gap-4')}>
        {cards.map((c) => {
          const cm = colorMap[c.color];
          const zero = c.value === 0;
          const tip = (c as { tip?: string }).tip;
          return (
            <button
              key={c.key}
              onClick={c.onClick}
              className={clsx(
                'group relative overflow-hidden text-left p-4 rounded-2xl border transition-all',
                isLight ? 'bg-white shadow-sm hover:shadow-md' : 'bg-white/5 hover:border-white/10',
                zero ? (isLight ? 'border-slate-100' : 'border-white/5') : cm.border
              )}
            >
              <span className={clsx('inline-flex p-2 rounded-lg mb-3', cm.icon)}>
                <c.icon size={18} />
              </span>
              <p className={clsx('text-3xl font-black tracking-tight', zero ? (isLight ? 'text-slate-300' : 'text-slate-600') : cm.num)}>
                {c.value}
              </p>
              <p className={clsx('text-xs font-semibold mt-1 leading-tight', isLight ? 'text-slate-700' : 'text-slate-200')}>{c.label}</p>
              <p className="text-[10px] text-slate-400 mt-0.5">{c.hint}</p>
              {tip && c.value > 0 && <HoverTip tip={tip} theme={theme} />}
            </button>
          );
        })}
      </div>
      )}
      </div>

      {hasLists && (
      <div className="mt-4 space-y-4 lg:col-span-3 lg:mt-0">
      {/* Priority nudge: share requirements inline. Matching depends on it. */}
      {showShareList && (
        <div className={clsx('rounded-2xl border overflow-hidden', isLight ? 'bg-amber-50/50 border-amber-200' : 'border-amber-500/20 bg-amber-500/5')}>
          <div className={clsx('px-5 py-3 flex items-center gap-2', isLight ? 'text-amber-800' : 'text-amber-300')}>
            <Share2 size={16} />
            <span className="text-sm font-bold">Comparte para recibir coincidencias</span>
          </div>
          <div className={clsx('divide-y', isLight ? 'divide-amber-100' : 'divide-white/5')}>
            {pendingShareItems.map(item => (
              <div key={item.id} className="flex items-center gap-3 px-5 py-3">
                <span className={clsx('flex-1 text-sm truncate', isLight ? 'text-slate-800' : 'text-slate-100')}>{stripHtml(item.name) || 'Requerimiento sin nombre'}</span>
                <button
                  onClick={() => shareRequirement(item.id)}
                  disabled={sharingId === item.id}
                  className="shrink-0 flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-colors disabled:opacity-60"
                >
                  {sharingId === item.id ? <Loader2 size={14} className="animate-spin" /> : <Share2 size={14} />}
                  Compartir
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Overdue follow-ups: the actual list behind the "Seguimientos vencidos"
          number, with one-tap resolve so users stop living with a permanent
          red badge they can't act on. */}
      {showOverdueList && overdueList && (
        <div className={clsx('rounded-2xl border overflow-hidden', isLight ? 'bg-rose-50/50 border-rose-200' : 'border-rose-500/20 bg-rose-500/5')}>
          <div className={clsx('px-5 py-3 flex items-center gap-2', isLight ? 'text-rose-800' : 'text-rose-300')}>
            <AlarmClock size={16} />
            <span className="text-sm font-bold">Seguimientos vencidos</span>
          </div>
          <div className={clsx('divide-y max-h-80 overflow-y-auto', isLight ? 'divide-rose-100' : 'divide-white/5')}>
            {overdueList.map(c => (
              <div key={c.id} className="flex items-center gap-3 px-5 py-3">
                <div className="flex-1 min-w-0">
                  <p className={clsx('text-sm font-semibold truncate', isLight ? 'text-slate-900' : 'text-white')}>{c.name}</p>
                  <p className="text-xs text-slate-500 truncate">
                    {c.next_follow_up_note || 'Seguimiento'} · <span className="text-rose-500 font-semibold">{overdueSince(c.next_follow_up_at)}</span>
                  </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    onClick={() => handleReschedule(c, 1)}
                    disabled={actingId === c.id}
                    title="Reprogramar para mañana 9:00"
                    className={clsx('text-[11px] font-bold px-2 py-1.5 rounded-lg border transition-colors disabled:opacity-50', isLight ? 'border-slate-200 text-slate-600 hover:bg-slate-100' : 'border-white/10 text-slate-300 hover:bg-white/10')}
                  >
                    Mañana
                  </button>
                  <button
                    onClick={() => handleReschedule(c, snoozeDays)}
                    disabled={actingId === c.id}
                    title={`Reprogramar a dentro de ${snoozeDays} días`}
                    className={clsx('text-[11px] font-bold px-2 py-1.5 rounded-lg border transition-colors disabled:opacity-50', isLight ? 'border-slate-200 text-slate-600 hover:bg-slate-100' : 'border-white/10 text-slate-300 hover:bg-white/10')}
                  >
                    +{snoozeDays} días
                  </button>
                  <button
                    onClick={() => handleComplete(c)}
                    disabled={actingId === c.id}
                    title="Marcar como realizado: se registra en el historial de seguimientos"
                    className="flex items-center gap-1 bg-emerald-500 hover:bg-emerald-400 text-white text-[11px] font-bold px-2.5 py-1.5 rounded-lg transition-colors disabled:opacity-60"
                  >
                    {actingId === c.id ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                    Completar
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming appointments detail */}
      {showAgenda && (
        <div className={clsx('rounded-2xl border overflow-hidden', isLight ? 'bg-white border-slate-100' : 'bg-white/5 border-white/5')}>
          <div className="flex items-center justify-between px-5 py-3">
            <span className={clsx('text-sm font-bold flex items-center gap-2', isLight ? 'text-slate-900' : 'text-white')}>
              <CalendarClock size={16} className="text-emerald-500" /> Tu agenda
            </span>
            <button onClick={() => navigate('/calendario')} className="text-indigo-500 text-xs font-bold hover:text-indigo-400 flex items-center gap-1">
              Ver calendario <ArrowRight size={12} />
            </button>
          </div>
          <div className={clsx('divide-y', isLight ? 'divide-slate-50' : 'divide-white/5')}>
            {appts.items.map(a => (
              <div key={a.id} className="flex items-center gap-3 px-5 py-3">
                <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-500 shrink-0">
                  {APPT_LABEL[a.type] || a.type}
                </span>
                <div className="flex-1 min-w-0">
                  <p className={clsx('text-sm font-semibold truncate', isLight ? 'text-slate-900' : 'text-white')}>{a.title}</p>
                  {a.contact_name && <p className="text-xs text-slate-500 truncate">{a.contact_name}</p>}
                </div>
                <span className="text-xs text-slate-400 shrink-0">{apptWhen(a.date, a.time)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      </div>
      )}
      </div>

      {sharedIds.size > 0 && (
        <p className="text-xs text-emerald-500 font-semibold mt-2 flex items-center gap-1 px-1">
          <Check size={14} /> {sharedIds.size} requerimiento{sharedIds.size === 1 ? '' : 's'} compartido{sharedIds.size === 1 ? '' : 's'} — ya aparece{sharedIds.size === 1 ? '' : 'n'} en la red.
        </p>
      )}
    </CollapsibleSection>
  );
};
